import { useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BaseAreaFields, CapabilityPicker } from "@/components/LeadFields";
import { LeadMap } from "@/components/AdminLeadMap";
import { FollowupApprovals } from "@/components/AdminFollowups";
import { SeedCard } from "@/components/AdminSeed";
import { FilterSelect, AU_STATES, CONTACT_OPTIONS } from "@/components/AdminFilters";
import { Plus, Upload, Download, Search, Pencil, Trash2, Check, X, Loader2, MailX, MailCheck } from "lucide-react";
import { toast } from "sonner";

const TEMPLATE = "business_name,abn,is_gst_registered,suburb,postcode,state,max_service_radius_km,is_interstate_capable,capabilities,corridors,email,phone,website_url,lat,lng\n"
  + "Example Removals,51824753556,true,Parramatta,2150,NSW,60,true,Local;Interstate;Packing,NSW-VIC;NSW-QLD,hello@example-removals.com.au,0400000000,https://example-removals.com.au,,\n";
const BLANK = { business_name: "", abn: "", is_gst_registered: true, suburb: "", state: "", postcode: "", lat: null, lng: null, max_service_radius_km: 50,
  is_interstate_capable: false, capabilities: [], corridors: [], email: "", phone: "", website_url: "", approved_for_leads: false };

function downloadTemplate() {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv" }));
  a.download = "movetender-removalist-import.csv";
  a.click();
}

function ListingDialog({ open, onClose, initial, caps, onSaved }) {
  const [v, setV] = useState(BLANK);
  const [cors, setCors] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    const i = initial ? { ...BLANK, ...initial, lat: initial.base_location?.coordinates[1], lng: initial.base_location?.coordinates[0] } : BLANK;
    setV(i); setCors((i.corridors || []).map((c) => `${c.origin_state}-${c.destination_state}`).join("; "));
  }, [open, initial]);
  const f = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    const corridors = cors.split(/[;,]/).map((s) => s.trim()).filter((s) => s.includes("-")).map((s) => { const [o, d] = s.split("-"); return { origin_state: o.trim(), destination_state: d.trim() }; });
    const { id, base_location, claim_status, is_verified, claimed_user_id, leads_sent, lead_stats, created_at, unsubscribed, last_lead_at, ...body } = v; // eslint-disable-line no-unused-vars
    try {
      await (initial ? api.put(`/admin/listings/${initial.id}`, { ...body, corridors }) : api.post("/admin/listings", { ...body, corridors }));
      toast.success(initial ? "Listing updated" : "Listing added"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="listing-dialog">
        <DialogHeader><DialogTitle>{initial ? "Edit listing" : "Add removalist listing"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Business name</Label><Input value={v.business_name} onChange={f("business_name")} data-testid="listing-name-input" /></div>
          <div className="space-y-1.5"><Label>ABN</Label><Input value={v.abn} onChange={f("abn")} placeholder="11 digits" data-testid="listing-abn-input" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={v.email} onChange={f("email")} data-testid="listing-email-input" /></div>
          <div className="space-y-1.5"><Label>Phone</Label><Input value={v.phone} onChange={f("phone")} data-testid="listing-phone-input" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Website</Label><Input value={v.website_url} onChange={f("website_url")} placeholder="https://" data-testid="listing-website-input" /></div>
        </div>
        <BaseAreaFields v={v} set={setV} prefix="listing" />
        <div className="space-y-2"><Label>Capabilities</Label><CapabilityPicker options={caps} value={v.capabilities} onChange={(c) => setV({ ...v, capabilities: c })} prefix="listing-cap" /></div>
        <div className="space-y-1.5"><Label>Interstate corridors (optional)</Label><Input value={cors} onChange={(e) => setCors(e.target.value)} placeholder="NSW-VIC; NSW-QLD" data-testid="listing-corridors-input" />
          <p className="text-xs text-muted-foreground">If set, interstate leads are only sent for these routes.</p></div>
        <div className="flex flex-wrap gap-6 text-sm">
          <label className="flex items-center gap-2"><Switch checked={v.is_gst_registered} onCheckedChange={(c) => setV({ ...v, is_gst_registered: c })} /> GST registered</label>
          <label className="flex items-center gap-2"><Switch checked={v.approved_for_leads} onCheckedChange={(c) => setV({ ...v, approved_for_leads: c })} data-testid="listing-approved-switch" /> Approved for lead emails</label>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} className="bg-kinetic hover:bg-kinetic-hover text-white" data-testid="listing-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportCard({ onFinished }) {
  const ref = useRef(null);
  const [imp, setImp] = useState(null);
  useEffect(() => {
    if (!imp || imp.status !== "running") return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/listings/import/${imp.id}`);
        setImp(data);
        if (data.status !== "running") { onFinished(); toast.success(`Import finished: ${data.created} added, ${data.updated} updated, ${data.errors} errors`); }
      } catch (e) {
        setImp((p) => ({ ...p, status: "interrupted" }));
        toast.error("Lost track of the import. Refresh to see the listings that were added.");
      }
    }, 2000);
    return () => clearInterval(t);
  }, [imp, onFinished]);
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { setImp((await api.post("/admin/listings/import", fd)).data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    ref.current.value = "";
  };
  return (
    <Card className="p-4 space-y-3" data-testid="listing-import-card">
      <div className="flex flex-wrap items-center gap-2">
        <input ref={ref} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="listing-import-file" />
        <Button variant="outline" onClick={() => ref.current.click()} disabled={imp?.status === "running"} className="gap-2" data-testid="listing-import-btn"><Upload className="h-4 w-4" /> Import CSV</Button>
        <Button variant="ghost" onClick={downloadTemplate} className="gap-2 text-muted-foreground" data-testid="listing-template-btn"><Download className="h-4 w-4" /> Template</Button>
        <span className="text-xs text-muted-foreground">Rows without lat/lng are located from suburb + postcode (about 1 second per row). Imported listings start not approved for leads.</span>
      </div>
      {imp && (
        <div className="space-y-2" data-testid="listing-import-progress">
          <Progress value={(imp.done / imp.total) * 100} />
          <p className="text-xs">{imp.status === "running" ? "Importing" : "Finished"}: {imp.done}/{imp.total} · {imp.created} added · {imp.updated} updated · {imp.errors} errors</p>
          {imp.log?.length > 0 && <pre className="text-[11px] bg-muted rounded p-2 max-h-32 overflow-auto whitespace-pre-wrap" data-testid="listing-import-log">{imp.log.join("\n")}</pre>}
        </div>
      )}
    </Card>
  );
}

const STATUS_STYLE = { Unclaimed: "bg-slate-100 text-slate-700", Pending: "bg-amber-100 text-amber-800", Claimed: "bg-emerald-100 text-emerald-800" };

function LeadResponse({ s, id }) {
  if (!s || !s.sent) return <span className="text-xs text-muted-foreground" data-testid={`lead-response-${id}`}>No leads yet</span>;
  return (
    <div className="text-xs space-y-1 min-w-[120px]" data-testid={`lead-response-${id}`}>
      <p><strong>{s.sent}</strong> sent · <strong>{s.opened}</strong> opened</p>
      <p><strong>{s.quoted}</strong> quoted <span className="text-muted-foreground">({s.quote_rate}%)</span></p>
      {s.claimed_after_lead && <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px]">Claimed</Badge>}
      {s.follow_up && <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]" data-testid={`lead-follow-up-${id}`}>Follow up</Badge>}
    </div>
  );
}

function ListingRow({ l, selected, toggle, onEdit, onReload }) {
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); onReload(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  return (
    <tr className="border-t border-border align-top" data-testid={`listing-row-${l.id}`}>
      <td className="p-2"><input type="checkbox" checked={selected} onChange={toggle} data-testid={`listing-select-${l.id}`} /></td>
      <td className="p-2 min-w-[180px]"><p className="font-semibold">{l.business_name}</p><p className="text-xs text-muted-foreground break-all">{l.email}{l.phone ? ` · ${l.phone}` : ""}</p>
        {l.abn && <p className="text-xs text-muted-foreground">ABN {l.abn}</p>}</td>
      <td className="p-2 text-xs whitespace-nowrap">{l.suburb} {l.state} {l.postcode}<br />{l.max_service_radius_km} km{l.is_interstate_capable ? " · interstate" : ""}</td>
      <td className="p-2"><Badge className={`${STATUS_STYLE[l.claim_status]} border-0`} data-testid={`listing-status-${l.id}`}>{l.claim_status}</Badge>
        {l.claim_status === "Pending" && (
          <div className="flex gap-1 mt-1.5">
            <Button size="sm" className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => act(() => api.post(`/admin/listings/${l.id}/claim-decision`, { approve: true }), "Claim approved")} data-testid={`listing-claim-approve-${l.id}`}><Check className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => act(() => api.post(`/admin/listings/${l.id}/claim-decision`, { approve: false }), "Claim rejected")} data-testid={`listing-claim-reject-${l.id}`}><X className="h-3.5 w-3.5" /></Button>
          </div>
        )}</td>
      <td className="p-2">
        {l.unsubscribed ? (
          <div className="space-y-1">
            <span className="text-xs text-destructive flex items-center gap-1" data-testid={`listing-unsubscribed-${l.id}`}><MailX className="h-3.5 w-3.5" /> Unsubscribed</span>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs gap-1" data-testid={`listing-resubscribe-${l.id}`}
              onClick={() => window.confirm(`Only re-subscribe ${l.business_name} if they have asked to receive lead emails again. Continue?`)
                && act(() => api.post(`/admin/listings/${l.id}/resubscribe`), "Re-subscribed to lead emails")}>
              <MailCheck className="h-3.5 w-3.5" /> Re-subscribe
            </Button>
          </div>
        )
          : <Switch checked={!!l.approved_for_leads} onCheckedChange={(c) => act(() => api.post("/admin/listings/lead-approval", { ids: [l.id], approved: c }), c ? "Approved for leads" : "Lead emails stopped")} data-testid={`listing-leads-switch-${l.id}`} />}</td>
      <td className="p-2"><LeadResponse s={l.lead_stats} id={l.id} /></td>
      <td className="p-2 whitespace-nowrap">
        <Button size="icon" variant="ghost" onClick={() => onEdit(l)} data-testid={`listing-edit-${l.id}`}><Pencil className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" onClick={() => window.confirm(`Delete ${l.business_name}?`) && act(() => api.delete(`/admin/listings/${l.id}`), "Listing deleted")} data-testid={`listing-delete-${l.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
      </td>
    </tr>
  );
}

const SOURCE_OPTIONS = [["google_places", "Google search"], ["csv_import", "CSV import"], ["manual", "Added by hand"], ["removalist_signup", "Removalist's own profile"]];

function DirectoryFilters({ f, set, facets, shown }) {
  const fc = facets || {};
  return (
    <div className="flex flex-wrap items-end gap-3" data-testid="listing-category-filters">
      <FilterSelect label="State" value={f.state} onChange={(v) => set("state", v)} testid="listing-state-filter" allLabel="All states" allCount={fc.total}
        options={AU_STATES.map((s) => [s, s, fc.states?.[s] || 0])} />
      <FilterSelect label="Approved for leads" value={f.leads} onChange={(v) => set("leads", v)} testid="listing-approved-filter" allCount={fc.total}
        options={[["approved", "Approved", fc.leads?.approved], ["not_approved", "Not approved", fc.leads?.not_approved], ["unsubscribed", "Unsubscribed", fc.leads?.unsubscribed]]} />
      <FilterSelect label="Contact details" value={f.contact} onChange={(v) => set("contact", v)} testid="listing-contact-filter" allLabel="Any" allCount={fc.total}
        options={CONTACT_OPTIONS.map(([k, l]) => [k, l, fc.contact?.[k]])} />
      <FilterSelect label="Where it came from" value={f.source} onChange={(v) => set("source", v)} testid="listing-source-filter" allLabel="Any source" allCount={fc.total}
        options={SOURCE_OPTIONS.map(([k, l]) => [k, l, fc.sources?.[k] || 0])} />
      <p className="text-sm text-muted-foreground pb-2" data-testid="listing-showing-count">Showing <strong className="text-foreground">{shown}</strong> of {fc.total ?? 0}</p>
    </div>
  );
}

export function DirectoryTab() {
  const [data, setData] = useState({ listings: [], counts: {}, capabilities: [] });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [f, setF] = useState({ state: "", leads: "", contact: "", source: "" });
  const setFilter = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [sort, setSort] = useState("");
  const [sel, setSel] = useState([]);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const load = useCallback(() => {
    api.get("/admin/listings", { params: { q, status, sort, ...f } }).then((r) => { setData(r.data); setSel([]); }).catch(() => {});
  }, [q, status, sort, f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const bulk = async (approved) => {
    try { const { data: r } = await api.post("/admin/listings/lead-approval", { ids: sel, approved }); toast.success(`${r.updated} listing(s) updated`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const all = data.listings.length > 0 && sel.length === data.listings.length;
  return (
    <div className="space-y-4" data-testid="directory-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, suburb, postcode, ABN" className="pl-9" data-testid="listing-search-input" /></div>
        {["", "Unclaimed", "Pending", "Claimed"].map((s) => (
          <Button key={s || "all"} size="sm" variant={status === s ? "default" : "outline"} onClick={() => setStatus(s)} data-testid={`listing-filter-${s || "all"}`}>
            {s || "All"}{s && data.counts[s] != null ? ` (${data.counts[s]})` : ""}</Button>
        ))}
        <Button onClick={() => setDialog({ open: true, initial: null })} className="bg-kinetic hover:bg-kinetic-hover text-white gap-2" data-testid="listing-add-btn"><Plus className="h-4 w-4" /> Add listing</Button>
      </div>
      <DirectoryFilters f={f} set={setFilter} facets={data.facets} shown={data.listings.length} />
      <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="listing-leads-filter">
        <span className="text-xs uppercase tracking-wider text-muted-foreground mr-1">Sort</span>
        {[["", "Newest"], ["leads", "Most leads"], ["quotes", "Most quotes"], ["follow_up", "Worth following up"]].map(([k, label]) => (
          <Button key={k || "newest"} size="sm" variant={sort === k ? "default" : "outline"} onClick={() => setSort(k)} data-testid={`listing-sort-${k || "newest"}`}>{label}</Button>
        ))}
      </div>
      <FollowupApprovals onChanged={load} />
      <LeadMap />
      <ImportCard onFinished={load} />
      <SeedCard onFinished={load} />
      {sel.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="listing-bulk-bar">
          <span>{sel.length} selected</span>
          <Button size="sm" onClick={() => bulk(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="listing-bulk-approve-btn">Approve for leads</Button>
          <Button size="sm" variant="outline" onClick={() => bulk(false)} data-testid="listing-bulk-unapprove-btn">Stop leads</Button>
        </div>
      )}
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="p-2"><input type="checkbox" checked={all} onChange={() => setSel(all ? [] : data.listings.map((l) => l.id))} data-testid="listing-select-all" /></th>
              <th className="p-2">Business</th><th className="p-2">Base &amp; radius</th><th className="p-2">Claim</th><th className="p-2">Lead emails</th><th className="p-2">Lead response</th><th className="p-2"></th></tr>
          </thead>
          <tbody>
            {data.listings.map((l) => <ListingRow key={l.id} l={l} selected={sel.includes(l.id)} toggle={() => setSel(sel.includes(l.id) ? sel.filter((x) => x !== l.id) : [...sel, l.id])}
              onEdit={(x) => setDialog({ open: true, initial: x })} onReload={load} />)}
            {data.listings.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground" data-testid="listing-empty">{Object.values(f).some(Boolean) || q || status ? "No listings match these filters." : "No listings yet. Add one or import a CSV."}</td></tr>}
          </tbody>
        </table>
      </Card>
      <ListingDialog open={dialog.open} initial={dialog.initial} caps={data.capabilities} onClose={() => setDialog({ open: false, initial: null })} onSaved={load} />
    </div>
  );
}
